import React, { FC, useEffect, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Col,
  Comment,
  Descriptions,
  Input,
  List,
  Row,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd'

import { StyledCollapse } from './styles'
import { convertNumberToCommaSeperated, getFileId } from '../../../utils'
import { useHistory, useParams } from 'react-router-dom'
import { IResultDetail } from '../../../types/results'
import { storageApi } from '../../../api'
import PATHS from '../../../routes/paths'
import GenerationBarChart from './GenerationBarChart'
import BatterySocBarCharts from './BatterySocBarCharts'
import RRevenueBarChart from '../../../components/RRevenueBarChart'

const descriptioniItems: { title: string; dataKey: keyof IResultDetail }[] = [
  {
    title: 'Total Energy Bought From Grid(Mwh)',
    dataKey: 'totalEnergyBoughtFromGridMwh',
  },
  {
    title: 'Total Stored Energy (Mwh)',
    dataKey: 'totalStoredEnergyMwh',
  },
  {
    title: 'Total Energy Sold to Grid (MWh)',
    dataKey: 'totalEnergySoldToGridMwh',
  },
  {
    title: 'Average Sell Price ($/Mwh)',
    dataKey: 'averageSellPriceDollarsPerMwh',
  },
  {
    title: 'Average Energy Unit Storage Cost ($/kwh)',
    dataKey: 'averageEnergyUnitStorageCostDollarsPerKwh',
  },
  {
    title: 'Average Energy Storage Cost per Power ($/kw)',
    dataKey: 'averageEnergyStorageCostPerPowerDollarsPerKw',
  },
  { title: 'Storage CAPEX ($)', dataKey: 'storageCapexDollars' },
  { title: 'Storage LCOS ($/Mwh)', dataKey: 'storageLcosDollarsPerMwh' },
  {
    title: 'Total Revenue from Energy Sales ($/Mwh)',
    dataKey: 'totalRevenueFromEnergySalesDollarsPerMwh',
  },
  {
    title: 'Total Revenue from Ancillary Services ($ / MW)',
    dataKey: 'totalRevenueFromAncillaryServicesDollarsPerMw',
  },
]

const ResultDetail: FC<{
  id?: string
  title?: string
  wideBatteryCharts?: boolean
  hideComments?: boolean
}> = ({ id, title = '', wideBatteryCharts = false, hideComments = false }) => {
  const history = useHistory()
  const { resultId: resultIdParam } = useParams<Record<string, string>>()
  const [isLoading, setIsLoading] = useState(true)
  const [result, setResult] = useState<IResultDetail>() // TODO: remove any
  const resultId = id || resultIdParam

  async function fetchResult() {
    try {
      setIsLoading(true)
      const resultResponse = await storageApi.fetchResult(resultId)
      console.log(resultResponse)
      setResult(resultResponse)
      setIsLoading(false)
    } catch {
      history.push(PATHS.results)
    }
  }

  useEffect(() => {
    fetchResult()
  }, [])

  if (isLoading || !result) return <Spin spinning />

  return (
    <div>
      <div className='mb-24'>
        <Typography.Title className='d-inline primary mr-8' level={5}>
          Result
        </Typography.Title>
        <Typography.Title className='d-inline primary' level={3}>
          {result.name}
        </Typography.Title>
      </div>
      <Card className='mb-12'>
        <Row justify='space-around' gutter={[40, 40]}>
          <Col>
            <Statistic
              title='Tot. Gen. Energy (MWh)'
              value={result.totalGeneratedEnergyMwh}
              precision={0}
            />
          </Col>
          <Col>
            <Statistic
              title='Tot. Stored Energy (MWh)'
              value={result.totalStoredEnergyMwh}
              precision={0}
            />
          </Col>
          <Col>
            <Statistic title='Total Revenue($)' value={result.totalRevenueDollars} precision={0} />
          </Col>
          <Col>
            <Statistic title='Total Cycle Number' value={result.totalCycleNumber} precision={0} />
          </Col>
          <Col>
            <Statistic
              title='Average Energy Cost($)'
              value={result.averageEnergyCostDollars}
              precision={0}
            />
          </Col>
          <Col>
            <Statistic
              title='Average Energy Revenue($)'
              value={result.averageEnergyRevenueDollars}
              precision={0}
            />
          </Col>
        </Row>
      </Card>

      <Card className='mb-12'>
        <Row className='mb-16'>
          <Typography.Title level={5} className='primary'>
            Total Generated Energy
          </Typography.Title>
          {/* <div className='ml-24'>
            <Tag color='green'>Renewable</Tag>
            <Tag color='volcano'>Fossil</Tag>
          </div> */}
        </Row>
        <Row gutter={40}>
          {Object.values(result.totalGeneratedEnergyByPlants[0]).map(
            (totalGeneratedEnergy, idx) => (
              <Col key={idx}>
                <Tag color='geekblue'>
                  GEN-{idx + 1}:<b> {convertNumberToCommaSeperated(totalGeneratedEnergy)} MWh</b>
                </Tag>
              </Col>
            ),
          )}
        </Row>
      </Card>

      <Descriptions className='mb-48' layout='vertical' bordered>
        {descriptioniItems.map(({ title, dataKey }) => (
          <Descriptions.Item key={dataKey} label={title}>
            {convertNumberToCommaSeperated(result[dataKey] as number)}
          </Descriptions.Item>
        ))}
      </Descriptions>

      <BatterySocBarCharts
        wideCharts={wideBatteryCharts}
        fileIdSocCount={result.socCountsPercentId}
        fileIdSocChargeCount={result.chargeCountsPercentPercentId}
        fileIdSocDischargeCount={result.dischargeCountsPercentPercentId}
        fileIdBatteryStates={result.batterystatesId}
        essCyclesYearly={result.essCyclesYearly}
        totalCycleNumber={result.totalCycleNumber}
        totalStoredEnergyMwh={result.totalStoredEnergyMwh}
        averageStoragedEnergyMwhPerYear={result.averageStoragedEnergyMwhPerYear}
        averageSoc={result.averageSoc}
        averageChargingCRate={result.averageChargingCRate}
        averageDischargeCRate={result.averageDischargeCRate}
      />

      <GenerationBarChart
        ids={result.plantDataIds.map(getFileId)}
        chargeDataId={result.chargeDataId}
        referenceLine={60}
      />

      <RRevenueBarChart revenueId={result.revenueDataId} />

      {!hideComments && (
        <StyledCollapse defaultActiveKey={1}>
          <StyledCollapse.Panel header='Comments' key={1}>
            <List
              itemLayout='horizontal'
              dataSource={result.comments}
              renderItem={(comment) => (
                <Comment
                  author={comment.userName}
                  avatar={<Avatar src='https://joeschmoe.io/api/v1/random' alt='Han Solo' />}
                  content={<p>{comment.comment}</p>}
                  datetime={<span>{new Date(comment.date).toLocaleDateString()}</span>}
                />
              )}
            />

            <Input.TextArea className='mb-12' placeholder='Add Comment' rows={5} />

            <Row justify='end'>
              <Button type='primary' disabled>
                Send
              </Button>
            </Row>
          </StyledCollapse.Panel>
        </StyledCollapse>
      )}
    </div>
  )
}

export default ResultDetail
